/**
 * Billing Service Module
 * 
 * Handles subscription queries and billing-related utilities.
 */

import { supabase } from '@/lib/supabase/client';

// Types for subscription data
export interface Subscription {
  id: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  parent_id: string;
  child_id: string;
  status: SubscriptionStatus;
  price_id: string;
  product_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  trial_start: string | null;
  trial_end: string | null;
  created_at: string;
  updated_at: string;
}

export type SubscriptionStatus = 
  | 'none' 
  | 'trialing' 
  | 'active' 
  | 'past_due' 
  | 'canceled' 
  | 'incomplete';

export interface Child {
  id: string;
  name: string;
  parent_id: string;
  subscription_status: SubscriptionStatus | null;
  birth_date: string | null;
  avatar_url: string | null;
  current_phase: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ChildWithSubscription extends Child {
  subscription?: Subscription | null;
}

/**
 * Get subscription for a specific child
 */
export async function getSubscriptionForChild(childId: string): Promise<{
  data: Subscription | null;
  error: Error | null;
}> {
  try {
    // Note: 'subscriptions' table created by migration 20260110_stripe_billing_tables.sql
    // Use maybeSingle() since child might not have a subscription
    const { data, error } = await (supabase as any)
      .from('subscriptions')
      .select('id, stripe_subscription_id, stripe_customer_id, parent_id, child_id, status, price_id, product_id, current_period_start, current_period_end, cancel_at_period_end, canceled_at, trial_start, trial_end, created_at, updated_at')
      .eq('child_id', childId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return { data: data as Subscription | null, error: null };
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return { data: null, error: error as Error };
  }
}

/**
 * Get all subscriptions for a parent
 */
export async function getParentSubscriptions(parentId: string): Promise<{
  data: Subscription[];
  error: Error | null;
}> {
  try {
    // Note: 'subscriptions' table created by migration 20260110_stripe_billing_tables.sql
    const { data, error } = await (supabase as any)
      .from('subscriptions')
      .select('id, stripe_subscription_id, stripe_customer_id, parent_id, child_id, status, price_id, product_id, current_period_start, current_period_end, cancel_at_period_end, canceled_at, trial_start, trial_end, created_at, updated_at')
      .eq('parent_id', parentId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return { data: (data || []) as Subscription[], error: null };
  } catch (error) {
    console.error('Error fetching parent subscriptions:', error);
    return { data: [], error: error as Error };
  }
}

/**
 * Get children with their subscription status.
 * Relies on RLS (is_parent_or_household_member) to scope access,
 * matching ChildContext behavior so household co-parents see shared children.
 */
export async function getChildrenWithSubscriptions(_parentId?: string): Promise<{
  data: ChildWithSubscription[];
  error: Error | null;
}> {
  try {
    const { data: children, error: childError } = await supabase
      .from('children')
      .select('id, name, parent_id, subscription_status, birth_date, avatar_url, current_phase, created_at, updated_at')
      .order('name');

    if (childError) throw childError;

    if (!children || children.length === 0) {
      return { data: [], error: null };
    }

    // Get subscriptions for these children
    // Note: 'subscriptions' table created by migration 20260110_stripe_billing_tables.sql
    const childIds = children.map(c => c.id);
    const { data: subscriptions, error: subError } = await (supabase as any)
      .from('subscriptions')
      .select('id, stripe_subscription_id, stripe_customer_id, parent_id, child_id, status, price_id, product_id, current_period_start, current_period_end, cancel_at_period_end, canceled_at, trial_start, trial_end, created_at, updated_at')
      .in('child_id', childIds);

    if (subError && subError.code !== 'PGRST116') {
      throw subError;
    }

    // Map subscriptions to children
    const subsByChild = new Map<string, Subscription>();
    ((subscriptions || []) as any[]).forEach((sub: any) => {
      const existing = subsByChild.get(sub.child_id);
      // Keep the most recent active/trialing subscription
      if (!existing || 
          (sub.status === 'active' || sub.status === 'trialing') && 
          (existing.status !== 'active' && existing.status !== 'trialing')) {
        subsByChild.set(sub.child_id, sub as Subscription);
      }
    });

    const result: ChildWithSubscription[] = children.map(child => ({
      ...child,
      subscription: subsByChild.get(child.id) || null,
      subscription_status: subsByChild.get(child.id)?.status || null,
    }));

    return { data: result, error: null };
  } catch (error) {
    console.error('Error fetching children with subscriptions:', error);
    return { data: [], error: error as Error };
  }
}

/**
 * Check if a child has an active subscription
 */
export function isChildSubscribed(child: Child | ChildWithSubscription): boolean {
  const status = child.subscription_status;
  return status === 'active' || status === 'trialing';
}

/**
 * Check if subscription status allows feature access
 */
export function hasActiveSubscription(status: SubscriptionStatus | null | undefined): boolean {
  return status === 'active' || status === 'trialing';
}

/**
 * Get human-readable status label
 */
export function getStatusLabel(status: SubscriptionStatus | null | undefined): string {
  const labels: Record<string, string> = {
    none: 'No Subscription',
    trialing: 'Trial',
    active: 'Active',
    past_due: 'Payment Due',
    canceled: 'Canceled',
    incomplete: 'Incomplete',
  };
  return labels[status || 'none'] || 'Unknown';
}

/**
 * Get status color for UI
 */
export function getStatusColor(status: SubscriptionStatus | null | undefined): {
  bg: string;
  text: string;
  border: string;
} {
  const colors: Record<string, { bg: string; text: string; border: string }> = {
    none: { bg: 'bg-black/[0.03]', text: 'text-[#636366]', border: 'border-black/[0.08]' },
    trialing: { bg: 'bg-[#111111]/15', text: 'text-[#1A1A1A]', border: 'border-[#1A1A1A]/30' },
    active: { bg: 'bg-[#34C759]/15', text: 'text-[#34C759]', border: 'border-[#34C759]/30' },
    past_due: { bg: 'bg-[#FF9500]/15', text: 'text-[#FF9500]', border: 'border-[#FF9500]/30' },
    canceled: { bg: 'bg-black/[0.03]', text: 'text-[#8E8E93]', border: 'border-black/[0.08]' },
    incomplete: { bg: 'bg-[#FF9500]/10', text: 'text-[#FF9500]', border: 'border-[#FF9500]/20' },
  };
  return colors[status || 'none'] || colors.none;
}

/**
 * Format date for display
 */
export function formatSubscriptionDate(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

/**
 * Get days remaining in current period
 */
export function getDaysRemaining(periodEnd: string | null | undefined): number | null {
  if (!periodEnd) return null;
  try {
    const end = new Date(periodEnd);
    const now = new Date();
    const diffMs = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  } catch {
    return null;
  }
}

/**
 * Check if subscription is in trial
 */
export function isInTrial(subscription: Subscription | null | undefined): boolean {
  if (!subscription) return false;
  return subscription.status === 'trialing' && !!subscription.trial_end;
}

/**
 * Check if subscription will cancel at period end
 */
export function willCancel(subscription: Subscription | null | undefined): boolean {
  if (!subscription) return false;
  return subscription.cancel_at_period_end === true;
}
